/**
 * The tenant the current route is scoped to, or null on a platform route.
 *
 * `orgId` lives in the path, never in a query param or in context alone, so
 * every tenant-scoped page is linkable, shareable and survives a refresh. The
 * previous shape — `/agents?organization_id=` and `/collections?org=`, two
 * different spellings — meant switching between those two pages silently
 * dropped the tenant you had just picked.
 */
import { useParams } from 'react-router-dom';

export function useCurrentOrgId(): string | null {
  const { orgId } = useParams<{ orgId: string }>();
  return orgId ?? null;
}

/**
 * Same, for pages that cannot render without a tenant.
 * Throws rather than returning a fallback: reaching one of these without an
 * `orgId` means the route table is wrong, and a silent empty state would hide
 * that until someone noticed the page was always blank.
 */
export function useRequiredOrgId(): string {
  const orgId = useCurrentOrgId();
  if (!orgId) {
    throw new Error('This page must be mounted under /orgs/:orgId.');
  }
  return orgId;
}
